import { ReactNode } from "react";
import Layout from "./Layout";
import styles from "../styles/Site.module.css";

interface BookReviewProps {
    text: (string | JSX.Element)[];
    title: string;
    quotes: Array<string>;
    rating: string;
    date: string;
    author: string;
    reviewed: boolean;
}

const QUOTE_CHUNK = /(&quot;[\s\S]*?&quot;|“[\s\S]*?”|"[\s\S]*?"|<i>[\s\S]*?<\/i>)/g;

const extractText = (item: string | JSX.Element): string => {
    if (typeof item === "string") {
        return item;
    }
    const children = (item.props as { children?: ReactNode }).children;
    return typeof children === "string" ? children : "";
};

const startsWithQuote = (text: string) => {
    const trimmed = text.trim();
    return trimmed.startsWith("“") || trimmed.startsWith("\"") || trimmed.startsWith("&quot;");
};

const formatInline = (text: string) => {
    return text
        .split(QUOTE_CHUNK)
        .filter(Boolean)
        .map((part, index) => {
            if (part.startsWith("&quot;")) {
                return <q key={index}>{part.slice(6, -6)}</q>;
            }
            if (part.startsWith("“") && part.endsWith("”")) {
                return <q key={index}>{part.slice(1, -1)}</q>;
            }
            if (part.startsWith("\"") && part.endsWith("\"")) {
                return <q key={index}>{part.slice(1, -1)}</q>;
            }
            if (part.startsWith("<i>")) {
                return <em key={index}>{part.slice(3, -4)}</em>;
            }
            return part;
        });
};

const stripOuterQuotes = (text: string) => {
    const trimmed = text.trim();
    if (trimmed.startsWith("“") && trimmed.endsWith("”")) {
        return trimmed.slice(1, -1);
    }
    if (trimmed.startsWith("\"") && trimmed.endsWith("\"")) {
        return trimmed.slice(1, -1);
    }
    if (trimmed.startsWith("&quot;") && trimmed.endsWith("&quot;")) {
        return trimmed.slice(6, -6);
    }
    return trimmed;
};

const BookReview = ({ text, title, quotes, date, author, reviewed }: BookReviewProps) => {
    const paragraphs = text.filter((item) => item !== "");

    return (
        <Layout>
            <h1 className={styles.pageTitle}><em>{title}</em></h1>
            <p className={styles.kicker}>
                {author} · {date}
            </p>
            {reviewed
                ? paragraphs.map((item, index) => {
                    const raw = extractText(item);
                    const quotedPassage = typeof item !== "string" || startsWithQuote(raw);
                    return quotedPassage
                        ? (
                            <blockquote key={index} className={styles.quote}>
                                <p>{stripOuterQuotes(raw)}</p>
                            </blockquote>
                        )
                        : <p key={index}>{formatInline(raw)}</p>;
                })
                : <p>Review pending.</p>
            }
            {quotes.length > 0 && (
                <>
                    <p>Favorite quotes from <em>{title}</em>:</p>
                    <blockquote className={styles.quote}>
                        {quotes.map((quote, index) => (
                            <p key={index}>{stripOuterQuotes(quote)}</p>
                        ))}
                    </blockquote>
                </>
            )}
        </Layout>
    );
};

export default BookReview;
